import { scaleBand, scaleUtc, utcFormat, utcMonth, utcYear } from 'd3';
import { clamp } from 'lodash';

import { Dimensions } from './stacked-bar-shared';

export type GroupAxisTick = {
  coord: number;
  label: string;
};

export type GroupAxisLayout = {
  isTime: boolean;
  getBarStart: (index: number) => number | undefined;
  barThickness: number;
  step: number;
  tickSpacing: number;
  ticks: GroupAxisTick[];
};

export const parseGroupDate = (label: string): Date | null => {
  const parsed = new Date(label);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTickYear = utcFormat('%Y');
const formatTickMonth = utcFormat('%b');
const formatTickDay = utcFormat('%b %d');

const formatTimeTick = (date: Date): string => {
  if (+utcMonth(date) < +date) return formatTickDay(date);
  if (+utcYear(date) < +date) return formatTickMonth(date);
  return formatTickYear(date);
};

type DimensionProps = {
  paddingInner: number;
  paddingOuter: number;
};
/**
 * Builds the group-axis layout for a stacked bar chart. Categorical charts use a
 * d3 band scale (one slot per bar). When `isTimeseries` is set and every group
 * parses to a date, a UTC time scale is used instead so bars sit at their real
 * temporal position and the axis renders time ticks. Falls back to the band scale
 * if the dates can't be parsed or there are fewer than two points.
 */
export const buildGroupAxisLayout = ({
  labels,
  isTimeseries,
  rangeLength,
  dimensions,
}: {
  labels: string[];
  isTimeseries: boolean;
  rangeLength: number;
  dimensions?: DimensionProps;
}): GroupAxisLayout => {
  const {
    paddingInner = Dimensions.BandPaddingFactor,
    paddingOuter = Dimensions.BandPaddingFactor,
  } = dimensions || {};
  const buildBand = (): GroupAxisLayout => {
    const indices = labels.map((_, index) => index.toString());
    const scale = scaleBand(indices, [0, rangeLength])
      .paddingOuter(paddingOuter)
      .paddingInner(paddingInner);
    return {
      isTime: false,
      getBarStart: (index) => scale(index.toString()),
      barThickness: scale.bandwidth(),
      step: scale.step(),
      tickSpacing: scale.step(),
      ticks: [],
    };
  };

  if (!isTimeseries) return buildBand();

  const dates = labels.map(parseGroupDate);
  if (dates.length < 2 || dates.some((date) => date === null)) {
    return buildBand();
  }
  const validDates = dates as Date[];
  const times = validDates.map((date) => date.getTime());
  const [minTime, maxTime] = [Math.min(...times), Math.max(...times)];
  if (minTime === maxTime) return buildBand();

  // Derive a bar thickness from the smallest gap between adjacent points, then
  // inset the scale by half that thickness so the first/last bars fit on-axis.
  const provisional = scaleUtc()
    .domain([minTime, maxTime])
    .range([0, rangeLength]);
  const sortedTimes = [...times].sort((a, b) => a - b);
  let minGap = rangeLength;
  for (let i = 1; i < sortedTimes.length; i++) {
    const current = sortedTimes[i];
    const previous = sortedTimes[i - 1];
    if (current === undefined || previous === undefined) continue;
    const gap = provisional(current) - provisional(previous);
    if (gap > 0) minGap = Math.min(minGap, gap);
  }
  const barThickness = clamp(
    minGap * (1 - Dimensions.BandPaddingFactor),
    Dimensions.MinBarThickness,
    Dimensions.MaxBarThickness
  );

  const inset = barThickness / 2;
  const scale = scaleUtc()
    .domain([minTime, maxTime])
    .range([inset, Math.max(inset, rangeLength - inset)]);

  // Budget ~140px of axis length per tick (capped low) so there are few, well-spaced
  // ticks whose labels comfortably fit instead of being truncated to ellipses.
  const tickCount = clamp(Math.round(rangeLength / 140), 2, 8);
  const ticks: GroupAxisTick[] = scale.ticks(tickCount).map((tick) => ({
    coord: scale(tick),
    label: formatTimeTick(tick),
  }));

  let tickSpacing = rangeLength;
  for (let i = 1; i < ticks.length; i++) {
    const current = ticks[i];
    const previous = ticks[i - 1];
    if (current === undefined || previous === undefined) continue;
    tickSpacing = Math.min(tickSpacing, current.coord - previous.coord);
  }

  return {
    isTime: true,
    getBarStart: (index) => {
      const date = validDates[index];
      if (date === undefined) return undefined;
      return scale(date) - barThickness / 2;
    },
    barThickness,
    step: minGap,
    tickSpacing,
    ticks,
  };
};
